import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  LayoutGrid,
  Users,
  Diamond,
  Square,
  AlertTriangle,
  Lock,
  Handshake,
  HelpCircle,
  FileText,
  Trash2,
  Eraser,
  Sparkles,
  Cpu,
  Copy,
  Check,
  ArrowRight,
  Info,
  X,
  Loader2,
  Share2,
  Save,
  Clock,
  PlayCircle,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  STAGES,
  LEVELS,
  TOKEN_DEFS,
  PLAYS,
  readField,
  composeFieldSummary,
  exampleField,
  planPlay,
  applyPlan,
  tokenDef,
  type StageId,
  type LevelId,
  type TokenType,
  type PlacedToken,
  type Accent,
  type Severity,
  type Tension,
} from '../data/fieldOfPlay';
import { narrateField } from '../lib/llmFieldReaderService';
import { useAiCapability } from '../lib/ai/useAiCapability';
import { encodeField, decodeField } from '../lib/fieldShare';

const STORAGE_KEY = 'fieldOfPlay.v1';
const CYCLES_KEY = 'fieldOfPlay.cycles.v1';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Users,
  Diamond,
  Square,
  AlertTriangle,
  Lock,
  Handshake,
  HelpCircle,
  FileText,
};

const STAGE_ACCENT: Record<Accent, { soft: string; head: string; bar: string }> = {
  sky: { soft: 'bg-[var(--slate-dim)]/60', head: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', bar: 'bg-[var(--slate)]' },
  indigo: { soft: 'bg-[var(--slate-dim)]/60', head: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', bar: 'bg-[var(--slate)]' },
  amber: { soft: 'bg-[var(--mustard-dim)]/60', head: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', bar: 'bg-[var(--mustard)]' },
  violet: { soft: 'bg-[var(--slate-dim)]/60', head: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', bar: 'bg-[var(--slate)]' },
  emerald: { soft: 'bg-[var(--green-dim)]/60', head: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', bar: 'bg-[var(--green)]' },
  teal: { soft: 'bg-[var(--green-dim)]/60', head: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', bar: 'bg-[var(--green)]' },
};

const TOKEN_ACCENT: Record<TokenDefAccent, string> = {
  sky: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  amber: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  indigo: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  red: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
  slate: 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]',
  emerald: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
  gray: 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]',
  teal: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
};

type TokenDefAccent = (typeof TOKEN_DEFS)[number]['accent'];

const SEVERITY: Record<Severity, { dot: string; chip: string; label: string }> = {
  info: { dot: 'bg-[var(--surface)]', chip: 'bg-[var(--surface)] border-[var(--rule)]', label: 'text-[var(--text-sec)]' },
  warn: { dot: 'bg-[var(--mustard)]', chip: 'bg-[var(--mustard-dim)] border-[var(--mustard)]', label: 'text-[var(--mustard)]' },
  risk: { dot: 'bg-[var(--coral)]', chip: 'bg-[var(--coral-dim)] border-[var(--coral)]', label: 'text-[var(--coral)]' },
};

const PRIORITY_DOT: Record<string, string> = {
  high: 'bg-[var(--coral)]',
  med: 'bg-[var(--mustard)]',
  low: 'bg-[var(--rule-bright)]',
};

function newId(): string {
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/* ------------------------------------------------------------------ */
/* Reading shape (shared by the rule engine and the LLM)              */
/* ------------------------------------------------------------------ */

interface DisplayReading {
  narrative: string;
  observations: string[];
  tensions: Tension[];
}

function rulesReading(tokens: PlacedToken[]): DisplayReading {
  const { observations, tensions } = readField(tokens);
  return { narrative: composeFieldSummary(tokens), observations, tensions };
}

interface SavedCycle {
  id: string;
  name: string;
  tokens: PlacedToken[];
  savedAt: string;
}

/* ------------------------------------------------------------------ */
/* Token chip                                                          */
/* ------------------------------------------------------------------ */

const TokenChip: React.FC<{
  token: PlacedToken;
  selected: boolean;
  pulsing: boolean;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  registerRef: (el: HTMLButtonElement | null) => void;
}> = ({ token, selected, pulsing, onClick, onDragStart, registerRef }) => {
  const def = tokenDef(token.type);
  const Icon = ICONS[def.icon] ?? Square;
  const dashed = token.confidence === 'low';
  return (
    <button
      ref={registerRef}
      draggable
      onDragStart={onDragStart}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={`${def.name}${token.owner ? ` · ${token.owner}` : ''}`}
      className={`group flex items-center gap-1.5 max-w-full pl-1.5 pr-2 py-1  border text-xs font-medium cursor-grab active:cursor-grabbing transition ${
        TOKEN_ACCENT[def.accent]
      } ${dashed ? 'border-dashed' : 'border-solid'} ${
        selected ? 'ring-2 ring-offset-1 ring-charcoal-400' : ''
      } ${pulsing ? 'ring-2 ring-offset-1 ring-consulting-slate-blue-500 shadow-md' : ''}`}
    >
      {token.priority && (
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_DOT[token.priority]}`} />
      )}
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="truncate">{token.label?.trim() || def.name}</span>
      {token.reversibility === 'irreversible' && <Lock className="h-2.5 w-2.5 flex-shrink-0 opacity-70" />}
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* Editor for a selected token                                        */
/* ------------------------------------------------------------------ */

const selectClass =
  'w-full  border border-[var(--rule)] px-2 py-1.5 text-sm text-charcoal-800 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';

const TokenEditor: React.FC<{
  token: PlacedToken;
  onChange: (patch: Partial<PlacedToken>) => void;
  onDelete: () => void;
  onClose: () => void;
}> = ({ token, onChange, onDelete, onClose }) => {
  const def = tokenDef(token.type);
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-charcoal-900 flex items-center gap-2">
          <span className={`px-1.5 py-0.5 rounded border text-xs ${TOKEN_ACCENT[def.accent]}`}>{def.name}</span>
          piece
        </p>
        <button onClick={onClose} className="text-charcoal-400 hover:text-charcoal-700" aria-label="Close editor">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1 block">Label</label>
          <input
            type="text"
            value={token.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder={def.name}
            className={selectClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Selector label="Stage" value={token.stage} onChange={(v) => onChange({ stage: v as StageId })}>
            {STAGES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Selector>
          <Selector label="Level" value={token.level} onChange={(v) => onChange({ level: v as LevelId })}>
            {LEVELS.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Selector>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Selector label="Priority" value={token.priority ?? ''} onChange={(v) => onChange({ priority: (v || undefined) as PlacedToken['priority'] })}>
            <option value="">—</option>
            <option value="low">Low</option>
            <option value="med">Medium</option>
            <option value="high">High</option>
          </Selector>
          <Selector label="Confidence" value={token.confidence ?? ''} onChange={(v) => onChange({ confidence: (v || undefined) as PlacedToken['confidence'] })}>
            <option value="">—</option>
            <option value="low">Low</option>
            <option value="med">Medium</option>
            <option value="high">High</option>
          </Selector>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Selector
            label="Reversibility"
            value={token.reversibility ?? ''}
            onChange={(v) => onChange({ reversibility: (v || undefined) as PlacedToken['reversibility'] })}
          >
            <option value="">—</option>
            <option value="reversible">Reversible</option>
            <option value="irreversible">Irreversible</option>
          </Selector>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1 block">Owner</label>
            <input
              type="text"
              value={token.owner ?? ''}
              onChange={(e) => onChange({ owner: e.target.value })}
              placeholder="unassigned"
              className={selectClass}
            />
          </div>
        </div>
      </div>

      <button
        onClick={onDelete}
        className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm font-medium text-[var(--coral)] hover:bg-[var(--coral-dim)] border border-[var(--coral)] py-2 transition"
      >
        <Trash2 className="h-4 w-4" />
        Remove piece
      </button>
    </div>
  );
};

const Selector: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}> = ({ label, value, onChange, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1 block">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
      {children}
    </select>
  </div>
);

/* ------------------------------------------------------------------ */
/* Field reader panel (LLM narration, with rule-engine fallback)      */
/* ------------------------------------------------------------------ */

const FieldReader: React.FC<{
  reading: DisplayReading;
  source: 'llm' | 'rules';
  loading: boolean;
  empty: boolean;
  onRunPlay: (playId: string) => void;
}> = ({ reading, source, loading, empty, onRunPlay }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reading.narrative);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-charcoal-900 p-5 text-white">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-white/60 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Field readout
          </p>
          <div className="flex items-center gap-2">
            <span
              title={
                source === 'llm'
                  ? 'Narrated by an LLM reading the field as JSON'
                  : 'Rule engine — no LLM reachable, showing the deterministic read'
              }
              className={`flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                source === 'llm' ? 'bg-consulting-slate-blue-500/30 text-white' : 'bg-[var(--bg-raised)]/10 text-white/70'
              }`}
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : source === 'llm' ? (
                <Sparkles className="h-3 w-3" />
              ) : (
                <Cpu className="h-3 w-3" />
              )}
              {loading ? 'Reading…' : source === 'llm' ? 'AI reading' : 'Rule-based'}
            </span>
            {!empty && (
              <button
                onClick={copy}
                className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 bg-[var(--bg-raised)]/10 hover:bg-[var(--bg-raised)]/20 transition"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
        </div>
        <p className="text-sm leading-relaxed text-white/95">{reading.narrative}</p>
        {reading.observations.length > 0 && (
          <ul className="mt-3 space-y-1 border-t border-white/10 pt-3">
            {reading.observations.map((o, i) => (
              <li key={i} className="text-xs text-white/70 flex gap-2">
                <span className="text-white/40">•</span>
                {o}
              </li>
            ))}
          </ul>
        )}
      </div>

      {reading.tensions.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
          <p className="text-sm font-semibold text-charcoal-900 mb-3">Tensions &amp; suggested moves</p>
          <ul className="space-y-2.5">
            {reading.tensions.map((t) => {
              const sev = SEVERITY[t.severity];
              const p = t.playId ? PLAYS.find((x) => x.id === t.playId) : undefined;
              return (
                <li key={t.id} className={` border p-3 ${sev.chip}`}>
                  <p className="text-sm text-charcoal-800 flex gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${sev.dot}`} />
                    <span>{t.message}</span>
                  </p>
                  {p && (
                    <button
                      onClick={() => onRunPlay(p.id)}
                      className="mt-2 ml-4 inline-flex items-center gap-1 text-xs font-semibold text-consulting-slate-blue-700 hover:text-consulting-slate-blue-900"
                    >
                      <PlayCircle className="h-3.5 w-3.5" />
                      Run play: {p.name}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */

export const FieldOfPlay: React.FC = () => {
  const fieldReaderEnabled = useAiCapability('field_reader');
  const [searchParams, setSearchParams] = useSearchParams();
  const [tokens, setTokens] = useState<PlacedToken[]>([]);
  const [activeTool, setActiveTool] = useState<TokenType | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activePlayId, setActivePlayId] = useState<string | null>(null);
  const [recentlyMoved, setRecentlyMoved] = useState<Set<string>>(new Set());
  const [planNote, setPlanNote] = useState<string | null>(null);

  // Reading state — rules immediately, LLM narration when it arrives.
  const [reading, setReading] = useState<DisplayReading>(() => rulesReading([]));
  const [readingSource, setReadingSource] = useState<'llm' | 'rules'>('rules');
  const [readingLoading, setReadingLoading] = useState(false);

  // Cycles + sharing.
  const [cycles, setCycles] = useState<SavedCycle[]>([]);
  const [cycleName, setCycleName] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [sharedNotice, setSharedNotice] = useState(false);

  // FLIP animation bookkeeping.
  const chipRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const firstRects = useRef<Map<string, DOMRect> | null>(null);
  const readingReq = useRef(0);

  // ── Initial load: a shared ?field= link wins over saved local state. ──
  useEffect(() => {
    const shared = searchParams.get('field');
    if (shared) {
      const decoded = decodeField(shared);
      if (decoded) {
        setTokens(decoded);
        setSharedNotice(true);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('field');
      setSearchParams(next, { replace: true });
    } else {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) setTokens(JSON.parse(raw) as PlacedToken[]);
      } catch {
        /* ignore malformed storage */
      }
    }
    try {
      const raw = localStorage.getItem(CYCLES_KEY);
      if (raw) setCycles(JSON.parse(raw) as SavedCycle[]);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the working field.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } catch {
      /* ignore quota errors */
    }
  }, [tokens]);

  // ── Read the field: rules first (instant), then LLM narration (debounced). ──
  useEffect(() => {
    const rules = rulesReading(tokens);
    setReading(rules);
    setReadingSource('rules');
    if (tokens.length === 0) {
      setReadingLoading(false);
      return;
    }
    const myReq = ++readingReq.current;
    setReadingLoading(true);
    const handle = window.setTimeout(async () => {
      if (!fieldReaderEnabled) {
        setReadingLoading(false);
        return;
      }
      const narration = await narrateField(tokens);
      if (readingReq.current !== myReq) return;
      if (narration) {
        setReading({
          narrative: narration.narrative,
          observations: narration.observations,
          tensions: narration.tensions,
        });
        setReadingSource('llm');
      }
      setReadingLoading(false);
    }, 700);
    return () => window.clearTimeout(handle);
  }, [tokens, fieldReaderEnabled]);

  // ── FLIP: animate any pieces that a play just moved. ──
  useLayoutEffect(() => {
    const first = firstRects.current;
    if (!first) return;
    firstRects.current = null;
    first.forEach((firstRect, id) => {
      const el = chipRefs.current.get(id);
      if (!el) return;
      const lastRect = el.getBoundingClientRect();
      const dx = firstRect.left - lastRect.left;
      const dy = firstRect.top - lastRect.top;
      if (!dx && !dy) return;
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.zIndex = '30';
      void el.getBoundingClientRect(); // force reflow so the start frame sticks
      requestAnimationFrame(() => {
        el.style.transition = 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)';
        el.style.transform = '';
        window.setTimeout(() => {
          el.style.transition = '';
          el.style.zIndex = '';
        }, 660);
      });
    });
  });

  const selected = tokens.find((t) => t.id === selectedId) ?? null;
  const activePlay = activePlayId ? PLAYS.find((p) => p.id === activePlayId) ?? null : null;

  const registerRef = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) chipRefs.current.set(id, el);
    else chipRefs.current.delete(id);
  }, []);

  const placeAt = (stage: StageId, level: LevelId) => {
    if (!activeTool) return;
    const id = newId();
    setTokens((prev) => [...prev, { id, type: activeTool, label: '', stage, level }]);
    setSelectedId(id);
  };

  const moveToken = (id: string, stage: StageId, level: LevelId) => {
    setTokens((prev) => prev.map((t) => (t.id === id ? { ...t, stage, level } : t)));
  };

  const patchToken = (id: string, patch: Partial<PlacedToken>) =>
    setTokens((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const deleteToken = (id: string) => {
    setTokens((prev) => prev.filter((t) => t.id !== id));
    setSelectedId(null);
  };

  // Run a play: animate the implicated pieces to their new positions.
  const runPlay = useCallback(
    (playId: string) => {
      setActivePlayId(playId);
      const plan = planPlay(playId, tokens);
      setPlanNote(plan.summary);
      if (plan.moves.length === 0) return;

      // Capture "first" rects before the layout changes.
      const rects = new Map<string, DOMRect>();
      for (const m of plan.moves) {
        const el = chipRefs.current.get(m.tokenId);
        if (el) rects.set(m.tokenId, el.getBoundingClientRect());
      }
      firstRects.current = rects;

      setTokens((prev) => applyPlan(prev, plan));
      setSelectedId(null);

      const moved = new Set(plan.moves.map((m) => m.tokenId));
      setRecentlyMoved(moved);
      window.setTimeout(() => setRecentlyMoved(new Set()), 1700);
    },
    [tokens],
  );

  // ── Cycles + sharing ──
  const persistCycles = (next: SavedCycle[]) => {
    setCycles(next);
    try {
      localStorage.setItem(CYCLES_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const saveCycle = () => {
    if (tokens.length === 0) return;
    const name = cycleName.trim() || `Cycle ${cycles.length + 1}`;
    const entry: SavedCycle = { id: `c-${Date.now()}`, name, tokens, savedAt: new Date().toISOString() };
    persistCycles([entry, ...cycles].slice(0, 24));
    setCycleName('');
  };

  const loadCycle = (id: string) => {
    const c = cycles.find((x) => x.id === id);
    if (!c) return;
    setTokens(c.tokens);
    setSelectedId(null);
    setActiveTool(null);
  };

  const deleteCycle = (id: string) => persistCycles(cycles.filter((c) => c.id !== id));

  const shareField = async () => {
    if (tokens.length === 0) return;
    const url = `${window.location.origin}${window.location.pathname}?field=${encodeField(tokens)}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface)] py-8">
      <div className="max-w-7xl mx-auto px-4 space-y-10">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <LayoutGrid className="h-12 w-12 text-consulting-slate-blue-600" />
            <h1 className="text-4xl font-bold text-charcoal-900">Field of Play</h1>
          </div>
          <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
            A coach's clipboard for business. Place pieces on a stable field where{' '}
            <em>position carries meaning</em> — the situation becomes visible before anyone explains it, the field reads
            itself back to you, and a play moves the pieces to where they should be.
          </p>
        </div>

        {sharedNotice && (
          <div className="bg-consulting-slate-blue-50 border border-consulting-slate-blue-200 p-3 flex items-center gap-2 text-sm text-consulting-slate-blue-800">
            <Share2 className="h-4 w-4 flex-shrink-0" />
            Loaded a shared field from the link. Save it as a cycle to keep it.
            <button onClick={() => setSharedNotice(false)} className="ml-auto text-consulting-slate-blue-500 hover:text-consulting-slate-blue-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Toolbar: the palette */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mr-1">Pieces</span>
            {TOKEN_DEFS.map((def) => {
              const Icon = ICONS[def.icon] ?? Square;
              const on = activeTool === def.type;
              return (
                <button
                  key={def.type}
                  onClick={() => setActiveTool(on ? null : def.type)}
                  title={def.meaning}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5  border text-xs font-medium transition ${
                    on ? `${TOKEN_ACCENT[def.accent]} ring-2 ring-offset-1 ring-charcoal-400` : TOKEN_ACCENT[def.accent]
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {def.name}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => {
                  setTokens(exampleField());
                  setSelectedId(null);
                  setActiveTool(null);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-100 hover:bg-consulting-slate-blue-100 transition"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Load example
              </button>
              <button
                onClick={() => {
                  setTokens([]);
                  setSelectedId(null);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--hover-tint)] text-charcoal-600 hover:bg-[var(--hover-tint)] transition"
              >
                <Eraser className="h-3.5 w-3.5" />
                Clear
              </button>
            </div>
          </div>
          <p className="text-xs text-charcoal-500 mt-2">
            {activeTool ? (
              <>
                Placing <strong>{tokenDef(activeTool).name}</strong> — click a cell to drop it. Click a piece to edit;
                drag to move.
              </>
            ) : (
              <>Select a piece above, then click a cell to place it. Click a placed piece to edit it; drag to move.</>
            )}
          </p>
        </div>

        {/* Cycles & sharing */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1 block">
                Save this field as a cycle
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cycleName}
                  onChange={(e) => setCycleName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveCycle()}
                  placeholder={`Cycle ${cycles.length + 1} — e.g. "Q3 planning"`}
                  className={selectClass}
                />
                <button
                  onClick={saveCycle}
                  disabled={tokens.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition disabled:opacity-40 whitespace-nowrap"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save cycle
                </button>
              </div>
            </div>
            <button
              onClick={shareField}
              disabled={tokens.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)] transition disabled:opacity-40 whitespace-nowrap"
            >
              {shareCopied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
              {shareCopied ? 'Link copied' : 'Copy share link'}
            </button>
          </div>

          {cycles.length > 0 && (
            <div className="mt-4 border-t border-[var(--rule)] pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-2">Saved cycles</p>
              <div className="flex flex-wrap gap-2">
                {cycles.map((c) => (
                  <div
                    key={c.id}
                    className="group flex items-center gap-2 border border-[var(--rule)] bg-[var(--surface)] pl-3 pr-1.5 py-1.5"
                  >
                    <button onClick={() => loadCycle(c.id)} className="flex items-center gap-2 text-left">
                      <Clock className="h-3.5 w-3.5 text-charcoal-400" />
                      <span className="text-xs font-medium text-charcoal-800">{c.name}</span>
                      <span className="text-[10px] text-charcoal-400">
                        {c.tokens.length} piece{c.tokens.length === 1 ? '' : 's'} ·{' '}
                        {new Date(c.savedAt).toLocaleDateString()}
                      </span>
                    </button>
                    <button
                      onClick={() => deleteCycle(c.id)}
                      className="text-charcoal-300 hover:text-[var(--coral)] transition"
                      aria-label={`Delete ${c.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* The field + side panel */}
        <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
          {/* Field surface */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 overflow-x-auto">
            <div
              className="grid gap-1.5 min-w-[760px]"
              style={{ gridTemplateColumns: '110px repeat(6, minmax(0, 1fr))' }}
            >
              {/* Corner */}
              <div className="text-[10px] font-semibold uppercase tracking-wide text-charcoal-400 flex items-end pb-1 px-1">
                Level ╲ Stage
              </div>
              {/* Stage headers */}
              {STAGES.map((s) => {
                const a = STAGE_ACCENT[s.accent];
                return (
                  <div key={s.id} className="text-center">
                    <div className={` px-1 py-1 ${a.head}`}>
                      <p className="text-xs font-bold leading-tight">{s.name}</p>
                    </div>
                    <p className="text-[10px] text-charcoal-400 mt-1 leading-tight hidden sm:block">{s.question}</p>
                  </div>
                );
              })}

              {/* Rows */}
              {LEVELS.map((level) => (
                <React.Fragment key={level.id}>
                  <div className="flex flex-col justify-center pr-1">
                    <p className="text-xs font-bold text-charcoal-700 leading-tight">{level.name}</p>
                    <p className="text-[10px] text-charcoal-400 leading-tight hidden sm:block">{level.description}</p>
                  </div>
                  {STAGES.map((stage) => {
                    const a = STAGE_ACCENT[stage.accent];
                    const cellTokens = tokens.filter((t) => t.stage === stage.id && t.level === level.id);
                    return (
                      <div
                        key={stage.id}
                        onClick={() => placeAt(stage.id, level.id)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const id = e.dataTransfer.getData('text/plain');
                          if (id) moveToken(id, stage.id, level.id);
                        }}
                        className={`min-h-[88px]  border border-dashed border-[var(--rule)] p-1.5 flex flex-wrap content-start gap-1 transition ${a.soft} ${
                          activeTool ? 'cursor-copy hover:border-consulting-slate-blue-300' : ''
                        }`}
                      >
                        {cellTokens.map((t) => (
                          <TokenChip
                            key={t.id}
                            token={t}
                            selected={t.id === selectedId}
                            pulsing={recentlyMoved.has(t.id)}
                            onClick={() => setSelectedId(t.id)}
                            onDragStart={(e) => e.dataTransfer.setData('text/plain', t.id)}
                            registerRef={(el) => registerRef(t.id, el)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3 text-[11px] text-charcoal-400">
              <Info className="h-3.5 w-3.5" />
              Horizontal = progression of coordinated action. Vertical = locus of concern. Where a piece sits is what it
              means.
            </div>
          </div>

          {/* Side panel: editor when a piece is selected, otherwise the reader */}
          <div className="space-y-4">
            {selected ? (
              <TokenEditor
                token={selected}
                onChange={(patch) => patchToken(selected.id, patch)}
                onDelete={() => deleteToken(selected.id)}
                onClose={() => setSelectedId(null)}
              />
            ) : null}
            <FieldReader
              reading={reading}
              source={readingSource}
              loading={readingLoading}
              empty={tokens.length === 0}
              onRunPlay={runPlay}
            />
          </div>
        </div>

        {/* Active play detail */}
        {activePlay && (
          <div className="bg-[var(--bg-raised)] border border-consulting-slate-blue-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-charcoal-900 flex items-center gap-2">
                <PlayCircle className="h-4 w-4 text-consulting-slate-blue-600" />
                Play: {activePlay.name}
              </p>
              <button onClick={() => setActivePlayId(null)} className="text-charcoal-400 hover:text-charcoal-700">
                <X className="h-4 w-4" />
              </button>
            </div>
            {planNote && (
              <p className="text-xs font-medium text-consulting-slate-blue-700 bg-consulting-slate-blue-50 border border-consulting-slate-blue-100 px-2.5 py-1.5 mb-2 inline-block">
                {planNote}
              </p>
            )}
            <p className="text-sm text-charcoal-600 mb-1">
              <span className="font-semibold text-charcoal-700">Run when: </span>
              {activePlay.trigger}
            </p>
            <p className="text-sm text-charcoal-600">
              <span className="font-semibold text-charcoal-700">The move: </span>
              {activePlay.move}
            </p>
            {activePlay.link && (
              <Link
                to={activePlay.link.to}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-consulting-slate-blue-700 hover:text-consulting-slate-blue-900"
              >
                {activePlay.link.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
            {activePlay.secondaryLink && (
              <Link
                to={activePlay.secondaryLink.to}
                className="mt-2 ml-0 inline-flex items-center gap-1 text-sm font-semibold text-consulting-slate-blue-700 hover:text-consulting-slate-blue-900"
              >
                {activePlay.secondaryLink.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        )}

        {/* Plays catalog */}
        <section>
          <h2 className="text-2xl font-bold text-charcoal-900 mb-1">The plays</h2>
          <p className="text-charcoal-600 mb-5">
            Standard moves. Once the field shows the state, the play tells you what to do next — and moves the pieces to
            show it.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLAYS.map((p) => (
              <div key={p.id} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <p className="font-semibold text-charcoal-900">{p.name}</p>
                  <button
                    onClick={() => runPlay(p.id)}
                    title="Run this play on the field"
                    className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-consulting-slate-blue-700 hover:text-consulting-slate-blue-900"
                  >
                    <PlayCircle className="h-3.5 w-3.5" />
                    Run
                  </button>
                </div>
                <p className="text-xs text-charcoal-500 mb-2">
                  <span className="font-semibold text-charcoal-600">When: </span>
                  {p.trigger}
                </p>
                <p className="text-sm text-charcoal-700">{p.move}</p>
                {p.link && (
                  <Link
                    to={p.link.to}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-consulting-slate-blue-700 hover:text-consulting-slate-blue-900"
                  >
                    {p.link.label}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
                {p.secondaryLink && (
                  <Link
                    to={p.secondaryLink.to}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-consulting-slate-blue-700 hover:text-consulting-slate-blue-900"
                  >
                    {p.secondaryLink.label}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Legend: token grammar */}
        <section>
          <h2 className="text-2xl font-bold text-charcoal-900 mb-1">The grammar</h2>
          <p className="text-charcoal-600 mb-5">
            A small, learnable vocabulary. Shape is the class of thing; overlays carry its state.
          </p>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {TOKEN_DEFS.map((def) => {
                const Icon = ICONS[def.icon] ?? Square;
                return (
                  <div key={def.type} className="flex items-start gap-2.5">
                    <span className={`mt-0.5 flex items-center justify-center w-7 h-7  border ${TOKEN_ACCENT[def.accent]}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal-900 leading-tight">{def.name}</p>
                      <p className="text-xs text-charcoal-500 leading-tight">{def.meaning}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[var(--rule)] mt-5 pt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs text-charcoal-600">
              <p>
                <span className="inline-block w-2 h-2 rounded-full bg-[var(--coral)] mr-1.5 align-middle" />
                Coloured dot = priority
              </p>
              <p>
                <span className="inline-block w-5 border-t border-dashed border-charcoal-400 mr-1.5 align-middle" />
                Dashed border = low confidence
              </p>
              <p>
                <Lock className="h-3 w-3 inline mr-1.5 align-middle" />
                Lock = irreversible
              </p>
              <p>
                <span className="font-semibold">Position</span> = stage × level
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FieldOfPlay;
